<template>
  <div class="chatroom">
    <input 
      type="file" 
      :ref="`file`" 
      :accept="$accept_file_type.image + ',' + $accept_file_type.video"
      onclick="this.value = null" 
      @change="uploadFile"
    /> 
    <v-overlay z-index="99" :opacity=".9" :value="overlay.active" @click.native="overlay.active = false">
      <v-img 
        contain 
        v-if="overlay.active"
        :src="overlay.src" 
        class="no-background" 
        width="90vw" 
        height="90vh" 
        max-width="800px"
        max-height="800px"
        origin="center center"
      ></v-img>
    </v-overlay>

    <CustomizeHeader right>
      <div slot="left" v-if="roomData && roomData.member">
        <v-menu bottom  nudge-right="15" :close-on-content-click="false" content-class="profile-card">
          <template v-slot:activator="{ on, attrs }">
            <div class="d-flex align-center" v-bind="attrs" v-on="on">
              <v-avatar size="35" class="ml-4">
                <v-img :src="$HOST_URL + roomData.member.usericon"/>
              </v-avatar>
              <div class="nickname mx-2">{{ roomData.member.nickname }}</div>
              <v-icon small>expand_more</v-icon>
            </div>
          </template>
          <v-card class="pa-4">
            <v-list-item class="pa-0 mb-4">
              <v-list-item-avatar size="50" tile>
                <v-avatar tile>
                  <v-img :src="$HOST_URL + roomData.member.usericon"/>
                </v-avatar>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="font-weight-regular mb-1">
                  {{ roomData.member.nickname }}
                </v-list-item-title>
                <v-rating
                  x-small
                  dense
                  readonly
                  half-increments
                  :value="5"
                  background-color="yellow darken-2"
                  color="yellow darken-2"
                ></v-rating>
              </v-list-item-content>
            </v-list-item>
            <span @click="$store.dispatch('seller/block', roomData.member.id)">
              <v-btn v-if="!isBlock" dark block color="red darken-2" class="d-block mx-auto mt-4">封鎖</v-btn>
              <v-btn v-else dark block outlined color="red darken-2" class="d-block mx-auto mt-4">已封鎖</v-btn>
            </span>
          </v-card>
        </v-menu>
      </div>
      <template slot="right">
        <v-btn icon>
          <v-icon>more_vert</v-icon>
        </v-btn>
      </template>
    </CustomizeHeader>
    <div class="chat-container">
      <div class="body" ref="body" v-infinite-scroll="getChatRecords" infinite-scroll-disabled="busy" infinite-scroll-distance="150" infinite-scroll-reverse>
        <div
          v-for="record in records"
          :key="record.id"
          :class="['message', record.sender === 'member' ? 'a' : 'b']"
        >
          <div class="text light" v-once v-if="record.type == 'text'">
            {{ record.text }}
          </div>
          <div class="gift" v-else-if="record.type == 'gift'">
            送出一個
            <img 
              v-once
              :src="$HOST_URL + record.gift.image_path"
            ></img>
          </div>
          <img 
            v-else-if="record.type == 'image'"
            v-once
            :src="record.image_path.startsWith('blob') ? record.image_path : $HOST_URL + record.image_path"
            @click="overlay = {active: true, src: record.image_path.startsWith('blob') ? record.image_path : $HOST_URL + record.image_path, transition: false}"
          ></img>
          <audio 
            v-else-if="record.type == 'audio'"
            :src="record.audio_path.startsWith('blob') ? record.audio_path : $HOST_URL + record.audio_path" 
            controls 
            controlsList="nodownload noplaybackrate"
            type="audio/wav"
          ></audio>
          <v-responsive 
            v-else-if="record.type == 'video'"
            :width="$vuetify.breakpoint.xsOnly ? 300 : 400"
            :max-width="$vuetify.breakpoint.xsOnly ? 300 : 400" 
            :max-height="$vuetify.breakpoint.xsOnly ? 200 : 300"
          >
            <video-player
              class="video-player vjs-big-play-centered" 
              v-once
              :playsinline="true" 
              :options="$func.videoOption({
                src: record.video_path.startsWith('blob') ? record.video_path : $HOST_URL + record.video_path,
                type: record.video_type,
                bigPlayButton: true,
                autoplay: false,
                loop: false,
                controls: true,
              })"></video-player>
          </v-responsive>
          <v-card 
            v-else-if="record.type == 'product'"
            class="story-item" 
            flat 
            @click="$root.$emit('story', seller, record.product, record.product.product_type)"
          >
            <v-img
              :src="$func.productImagePreview(record.product)"
              gradient="to bottom, rgba(0,0,0,.1), rgba(0,0,0,.4)"
              width="150px"
              aspect-ratio="0.75"
              class="align-end"
            >
              <div class="description" :title="record.product.title">{{ record.product.title }}</div>
            </v-img>
          </v-card>
          <span class="created_at" v-if="$func.dayDiff(record.created_at) === 0">
            今天 {{ record.created_at | time }}
          </span>
          <span class="created_at" v-else-if="$func.dayDiff(record.created_at) === 1">
            昨天 {{ record.created_at | time }}
          </span>
          <span class="created_at"  v-else-if="$func.isThisYear(record.created_at)">
            {{ record.created_at | dateTime }}
          </span>
          <span class="created_at"  v-else>{{ record.created_at | dateTime }}</span>
        </div>
      </div>
      <div class="action">
        <div class="audio-timer" :style="`width: ${recordingTime / $audio_length.max * 100}%`"></div>
        <v-menu nudge-top="140" content-class="attach elevation-0">
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon dark class="mr-2" v-on="on">
              <v-icon>fa-paperclip</v-icon>
            </v-btn>
          </template>
          <v-btn icon dark x-large class="indigo elevation-6" @click="$refs.unlockLibrary.open()">
            <v-icon size="30" class="material-icons-outlined">lock_open</v-icon>
          </v-btn>
          <v-btn icon dark x-large class="purple elevation-6" @click="$refs.file.click()">
            <v-icon size="25">fa-image</v-icon>
          </v-btn>
        </v-menu>
        <div class="input-block">
          <div v-if="isRecording" class="white--text">
            {{ $moment.utc(recordingTime * 1000).format('mm:ss') }}
          </div>
          <template v-else>
            <v-textarea
              v-model="text"
              ref="text"
              solo
              rounded
              dense
              hide-details
              auto-grow
              no-resize
              rows="1"
              @keydown.enter="sendMessage('text', text)"
            ></v-textarea>
          </template>
        </div>
        <v-btn v-if="text" icon color="pink" class="ml-2" @click="sendMessage('text', text)">
          <v-icon>send</v-icon>
        </v-btn>
        <v-btn v-else icon dark class="ml-2">
          <v-icon v-if="!isRecording" @click="$store.dispatch('recorder/startRecording')">fa-microphone</v-icon>
          <v-icon v-else color="red" @click="$store.dispatch('recorder/stopRecording', $refs.chatroomUploadFile.open)">fa-stop</v-icon>
        </v-btn>
      </div>
    </div>
    <ChatroomUploadFile ref="chatroomUploadFile" @send="file => sendMessage(file.type.split('/')[0], file)"></ChatroomUploadFile>
    <UnlockLibrary ref="unlockLibrary" @send="product => sendMessage('product', product)"></UnlockLibrary>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import CustomizeHeader from '@/components/CustomizeHeader'
import ChatroomUploadFile from '@/components/ChatroomUploadFile'
import UnlockLibrary from '@/components/portal/UnlockLibrary'
export default {
  props: ['room', 'room_id'],
  components: { CustomizeHeader, ChatroomUploadFile, UnlockLibrary },
  data() {
    return {
      busy: false,
      page: 1,
      text: '',
      recorder: null,
      overlay: {active: false},
      records: [],
      roomData: null
    }
  },
  channel() {
    return `private:seller.${ this.seller.id }`
  },
  echo: {
    SendToChat(response) {
      this.records.push(response.message)
      this.scrollToBottom()
    }
  },
  beforeRouteLeave(to, from, next) {
    this.$store.dispatch('recorder/stopRecording')
    next()
  },
  created() {
    if(this.room) {
      this.roomData = this.room
    } else if(this.room_id) {
      // 從 URL 的 room_id 獲取聊天室資料
      this.fetchRoomData(this.room_id)
    } else {
      this.$router.push('/portal/chats')
    }
  },
  mounted() {
    this.markRead()
    // document.getElementsByClassName('chatroom')[0].style.height = window.innerHeight + 'px'
  },
  computed: {
    ...mapState('global', ['seller_job_types']),
    ...mapState('seller', ['seller', 'blockList']),
    ...mapState('recorder', ['isRecording', 'recordingTime']),
    isBlock() {
      if (!this.roomData || !this.roomData.member) return false
      return this.blockList.map(member => member.id).includes(this.roomData.member.id)
    },
  },
  watch: {
    recordingTime() {
      if(this.recordingTime >= this.$audio_length.max) {
        this.$store.dispatch('recorder/stopRecording', this.$refs.chatroomUploadFile.open)
      }
    },
    roomData(newVal) {
      if (newVal && newVal.id) {
        this.records = []
        this.page = 1
        this.busy = false
        this.getChatRecords()
        this.markRead()
      }
    }
  },
  methods: {
    fetchRoomData(roomId) {
      this.axios(`/portal/chat/${roomId}`).then(response => {
        this.roomData = response.data
      }).catch(() => {
        this.$router.push('/portal/chats')
      })
    },
    getChatRecords() {
      if(!this.roomData || !this.roomData.id) {
        return
      }
      this.busy = true
      return this.axios(`/portal/chat/${this.roomData.id}/message?page=${this.page}&size=50`).then(async response => {
        this.records.unshift(...response.data.data.reverse())
        if(this.page == 1) {
          await this.scrollToBottom(true)
        }
        else {
          await this.adjustScrollTop(this.$refs.body.scrollHeight, this.$refs.body.scrollTop)
        }
        if(this.page != response.data.last_page) {
          this.page += 1
          this.busy = false
        }
      })
    },
    markRead() {
      if(!this.roomData || !this.roomData.id) return
      this.axios.put(`/portal/chat/${this.roomData.id}/read`)
    },
    async uploadFile() {
      let file = event.target.files[0]
      if(!await this.$func.fileValidation(file, 'chatroom_seller')) {
        return
      }
      this.$refs.chatroomUploadFile.open(file)
    },
    scrollToBottom(first = false) {
      return new Promise(resolve => {
        this.$nextTick(() => {
          if(first) {
            Promise.all(
              [...this.$refs.body.getElementsByTagName('img')]
              .map(async img => {
                return new Promise(resolve => { 
                  let interval = setInterval(() => {
                    if(img.naturalWidth > 0 && img.naturalHeight > 0) {
                      clearInterval(interval)
                      resolve()
                    }
                  }, 100)
                })
              })
            )
            .then(() => {
              this.$refs.body.scrollTop = this.$refs.body.scrollHeight
              this.$refs.body.style.visibility = 'visible'
              resolve()
            })
          }
          else {
            this.$refs.body.scrollTop = this.$refs.body.scrollHeight
            resolve()
          }
        })
      })
    },
    // prevent auto scroll when prepend records
    adjustScrollTop(prev_height, prev_top) {
      return new Promise((resolve, reject) => {
        this.$nextTick(() => {
          this.$refs.body.scrollTop = this.$refs.body.scrollHeight - (prev_height - prev_top)
          resolve()
        })
      })
    },
    sendMessage(type, data) {
      // shift + enter
      if(event.keyCode == 13 && event.shiftKey) {
        return
      }
      else {
        event.preventDefault()
      }
      let formData = new FormData()
      formData.append('type', type)
      if(['text', 'image', 'video'].includes(type)) {
        formData.append(type, data)
      }
      else if(type == 'audio') {
        formData.append(type, data, 'audio.wav')
      }
      else if(type == 'product') {
        formData.append(`${type}_id`, data.id)
      }
      let payload = {
        url: `/portal/chat/${this.roomData.member.id}`,
        method: 'POST',
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        data: formData
      }
      return this.axios(payload).then(response => {
        if(type == 'text') {
          this.text = ''
        }
        let record = {type: type}
        if(type == 'text') {
          record[`text`] = data
        }
        else if(['image', 'video', 'audio'].includes(type)) {
          record[`${type}_path`] = URL.createObjectURL(data)
          record[`${type}_type`] = data.type
        }
        else if(type == 'product') {
          record.product = data
        }
        console.log(record)
        this.records.push(record)
        this.scrollToBottom()
      }).catch(e => {
        this.$root.$emit('alert', 'error', '發送失敗')
      })
    }
  }
}
</script>

<style lang="scss" scoped>
html {
  // overflow-y: hidden;
  // max-height: calc(100vh - 115px);
}
.attach {
  background: transparent !important;
  .v-btn {
     display: block;
     margin: 8px 0;
  }
}
.chatroom {
  // max-height: calc(100vh - 175px);
  .chat-container {
    height: calc(100vh - 115px);
    max-height: calc(100vh - 115px);
  }
  .v-overlay__content {
    max-width: 100%;
  }
}
</style>